// Clinic-wide list of in-progress encounters. Backed by
// GET /clinician/v1/encounters/work-list (added in Week 3). Rows show the
// patient's name + how long the encounter has been open. Clicking a row
// navigates into encounter detail. A quick-search input filters
// client-side by name / phone / chief complaint so a busy front desk can
// jump to a patient without scrolling. Each row also has a Cancel button
// that flips the encounter to status='cancelled' via
// POST /encounters/:id/cancel (Week 7 addition).

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, encountersApi, type WorkListEncounter } from '../lib/api';

export function WorkListScreen() {
    const [search, setSearch] = useState('');
    const { data, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: ['work-list'],
        queryFn: () => encountersApi.workList(),
    });

    // Client-side filter — the list is capped at 100 by the server so we
    // can afford an in-memory match rather than a round-trip. Match against
    // name, phone, and chief complaint so a receptionist can search by any
    // of the three.
    const filtered = useMemo(() => {
        const list = data?.encounters ?? [];
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter((e) => {
            const name = e.patient?.full_name?.toLowerCase() ?? '';
            const phone = e.patient?.phone_e164?.toLowerCase() ?? '';
            const cc = e.chief_complaint?.toLowerCase() ?? '';
            return name.includes(q) || phone.includes(q) || cc.includes(q);
        });
    }, [data, search]);

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-slate-900 text-2xl font-semibold">Active work</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Encounters in progress across your clinic.
                    </p>
                </div>
                <div className="flex gap-2 shrink-0">
                    <button
                        type="button"
                        className="pm-btn-outline whitespace-nowrap"
                        onClick={() => void refetch()}
                        disabled={isFetching}
                    >
                        {isFetching ? 'Refreshing…' : 'Refresh'}
                    </button>
                    <Link to="/encounters/new" className="pm-btn-primary whitespace-nowrap">
                        New encounter
                    </Link>
                </div>
            </div>

            {/* Quick search — always visible so it survives the loading
                state and doesn't jump the layout once the list arrives. */}
            <div className="pm-card p-3">
                <label htmlFor="worklist-search" className="sr-only">
                    Search active encounters
                </label>
                <input
                    id="worklist-search"
                    type="search"
                    className="pm-input"
                    placeholder="Search by patient name, phone, or complaint"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {isLoading && (
                <div className="pm-card p-8 text-center text-slate-500 text-sm">
                    Loading…
                </div>
            )}

            {isError && (
                <div className="pm-card p-6 text-center">
                    <div className="text-slate-800 font-medium">Can’t load the work list</div>
                    <div className="text-slate-500 text-sm mt-1">
                        Check your connection and try again.
                    </div>
                </div>
            )}

            {data && data.encounters.length === 0 && (
                <div className="pm-card p-8 text-center">
                    <div className="text-slate-800 font-medium">Nothing in progress</div>
                    <div className="text-slate-500 text-sm mt-1">
                        Start a new encounter to see it here.
                    </div>
                </div>
            )}

            {data && data.encounters.length > 0 && filtered.length === 0 && (
                <div className="pm-card p-6 text-center">
                    <div className="text-slate-800 font-medium">No matches</div>
                    <div className="text-slate-500 text-sm mt-1">
                        Try a different name, phone number, or complaint.
                    </div>
                </div>
            )}

            {data && filtered.length > 0 && (
                <ul className="pm-card divide-y divide-slate-100 overflow-hidden">
                    {filtered.map((e) => (
                        <li key={e.id}>
                            <WorkListRow encounter={e} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function WorkListRow({ encounter }: { encounter: WorkListEncounter }) {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [cancelError, setCancelError] = useState<string | null>(null);
    const patientName = encounter.patient?.full_name?.trim() || encounter.patient?.phone_e164 || 'Unknown patient';
    const openFor = formatOpenFor(encounter.started_at);
    const typeLabel = ENCOUNTER_TYPE_LABELS[encounter.encounter_type] ?? encounter.encounter_type;

    const cancelMutation = useMutation({
        mutationFn: (reason: string | undefined) =>
            encountersApi.cancel(encounter.id, reason ? { reason } : {}),
        onSuccess: () => {
            // Drop the cancelled encounter from the work list and refresh
            // detail views. Both queries are cheap to invalidate.
            void qc.invalidateQueries({ queryKey: ['work-list'] });
            void qc.invalidateQueries({ queryKey: ['encounter', encounter.id] });
        },
        onError: (err) => {
            if (err instanceof ApiError) {
                if (err.code === 'insufficient_role') {
                    setCancelError('You don’t have permission to cancel this encounter.');
                } else if (err.code === 'encounter_completed') {
                    setCancelError('This encounter is already completed and can’t be cancelled.');
                } else {
                    setCancelError('Couldn’t cancel the encounter. Try again.');
                }
            } else {
                setCancelError('Couldn’t cancel the encounter. Try again.');
            }
        },
    });

    function onCancel(evt: React.MouseEvent) {
        // Stop the parent row click from navigating into the detail
        // screen while we're showing the confirm dialog.
        evt.stopPropagation();
        setCancelError(null);
        const reasonRaw = window.prompt(
            `Cancel ${patientName}'s encounter?\n\nOptional: type a short reason (no-show, wrong patient, etc.).`,
            '',
        );
        // window.prompt returns null on cancel, '' if the user hit OK with
        // an empty field — both are valid "no reason" paths; we only bail
        // on null (explicit cancel).
        if (reasonRaw === null) return;
        const reason = reasonRaw.trim();
        cancelMutation.mutate(reason || undefined);
    }

    return (
        <div className="flex items-stretch">
            <button
                type="button"
                onClick={() => navigate(`/encounters/${encounter.id}`)}
                className="flex-1 min-w-0 text-left flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-50 transition"
            >
                <div className="min-w-0">
                    <div className="text-slate-900 font-medium truncate">{patientName}</div>
                    <div className="text-slate-500 text-xs mt-0.5 flex items-center gap-2">
                        <span>{typeLabel}</span>
                        <span aria-hidden>·</span>
                        <span>Open {openFor}</span>
                        {encounter.chief_complaint && (
                            <>
                                <span aria-hidden>·</span>
                                <span className="truncate">{encounter.chief_complaint}</span>
                            </>
                        )}
                    </div>
                    {cancelError && (
                        <div className="text-rose-600 text-xs mt-1">{cancelError}</div>
                    )}
                </div>
                <div className="text-slate-400 text-xs shrink-0">In progress</div>
            </button>
            <button
                type="button"
                className="shrink-0 px-3 my-2 mr-2 rounded-md border border-rose-200 text-rose-700 text-xs font-medium hover:bg-rose-50 disabled:opacity-50"
                onClick={onCancel}
                disabled={cancelMutation.isPending}
                aria-label={`Cancel encounter for ${patientName}`}
            >
                {cancelMutation.isPending ? 'Cancelling…' : 'Cancel'}
            </button>
        </div>
    );
}

// Human-readable "opened X ago" for the row subtitle. Deliberately coarse —
// nurses scanning the list care about ballpark ("30m", "3h", "2d"), not
// second-precision.
function formatOpenFor(startedAt: string): string {
    const startedMs = new Date(startedAt).getTime();
    if (!Number.isFinite(startedMs)) return 'recently';
    const diffMin = Math.max(0, Math.round((Date.now() - startedMs) / 60_000));
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    return `${diffDay}d ago`;
}

// Client-side labels for the encounter_type enum. Kept local (rather than in
// shared/) because these are display-only, English-first, and only rendered
// on this screen right now — moving them into shared risks premature
// localisation churn when Week 5 visit-summary lands.
const ENCOUNTER_TYPE_LABELS: Record<string, string> = {
    general: 'General',
    antenatal_visit: 'Antenatal',
    postnatal_visit: 'Postnatal',
    vaccination: 'Vaccination',
    screening: 'Screening',
    follow_up: 'Follow-up',
    emergency: 'Emergency',
};
